import { apiClient as api } from './api';

export interface Product {
  SupersedeNoUOM?: string;
  SupersedeNo: string;
  UOM: string;
  Description: string;
  Price: number;
  PriceDesc1: string;
  PriceDesc2: string;
  ConvRate: number;
  NetContentLabel?: string;
  DeliveryCalUnit: number;
  MainPictureFull: string;
  MainPictureName: string;
  /**
   * Present only when the admin uploaded a mobile-specific photo (issue #126).
   * Undefined means there is nothing mobile-specific and MainPictureFull is used
   * on every screen size.
   */
  MobilePictureFull?: string;
  pathSupersede: string;
  Original: string;
  prodDetail?: string;
  DocName?: string;
}

export interface ProductFilters {
  category?: string;
  search?: string;
  priceLevel?: string;
}

export interface ProductResponse {
  success: boolean;
  data: Product[];
  count: number;
  category?: string;
  searchTerm?: string;
}

export interface ProductDetailResponse {
  success: boolean;
  data: Product;
}

export interface ProductCategory {
  Category: string;
  CategoryDescription: string;
}

export interface CategoriesResponse {
  success: boolean;
  data: ProductCategory[];
}

export interface CartValidationRequest {
  cartItems: Array<{
    SupersedeNo: string;
    UOM: string;
    Price: number;
    ConvRate: number;
    DeliveryCalUnit: number;
  }>;
  priceLevel?: string;
}

export interface CartValidationResponse {
  success: boolean;
  data: {
    validItems: Product[];
    invalidItems: string[];
    priceChanges: any[];
  };
  hasChanges: boolean;
}

// Request cache and throttling
interface CacheEntry {
  data: any;
  timestamp: number;
}

class RequestThrottler {
  private cache: Map<string, CacheEntry> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private readonly CACHE_DURATION = 30000; // 30 seconds
  private readonly CATEGORIES_CACHE_DURATION = 300000; // 5 minutes for categories (static data)
  private readonly THROTTLE_DELAY = 1000; // 1 second between same requests
  private lastRequestTime: Map<string, number> = new Map();

  async throttledRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check cache first with appropriate cache duration
    const cached = this.cache.get(key);
    const cacheDuration = key.includes('getCategories') 
      ? this.CATEGORIES_CACHE_DURATION 
      : this.CACHE_DURATION;
      
    if (cached && Date.now() - cached.timestamp < cacheDuration) {
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Check throttling
    const lastRequest = this.lastRequestTime.get(key) || 0;
    const timeSinceLastRequest = Date.now() - lastRequest;
    if (timeSinceLastRequest < this.THROTTLE_DELAY) {
      await new Promise(resolve => setTimeout(resolve, this.THROTTLE_DELAY - timeSinceLastRequest));
    }

    // Make the request with retry logic for 429 errors
    const requestPromise = this.executeWithRetry(requestFn, 3).then(result => {
      // Cache the result
      this.cache.set(key, { data: result, timestamp: Date.now() });
      this.lastRequestTime.set(key, Date.now());
      this.pendingRequests.delete(key);
      return result;
    }).catch(error => {
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, requestPromise);
    return requestPromise;
  }

  private async executeWithRetry<T>(requestFn: () => Promise<T>, maxRetries: number): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error: any) {
        lastError = error;
        
        // Check if it's a 429 rate limit error
        if (error.message?.includes('429') || error.status === 429) {
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const backoffDelay = Math.pow(2, attempt) * 1000;
            console.warn(`Rate limited, retrying after ${backoffDelay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            continue;
          }
        }
        
        // For non-429 errors or if we've exceeded max retries, throw immediately
        throw error;
      }
    }
    
    throw lastError!;
  }

  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

const requestThrottler = new RequestThrottler();

export class ProductService {
  /**
   * Get all products with optional filters
   */
  static async getAllProducts(filters: ProductFilters = {}): Promise<ProductResponse> {
    const params = new URLSearchParams();
    
    if (filters.category) {
      params.append('category', filters.category);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.priceLevel) {
      params.append('priceLevel', filters.priceLevel);
    }

    const cacheKey = `getAllProducts:${params.toString()}`;
    return requestThrottler.throttledRequest(cacheKey, () => 
      api.get(`/products?${params.toString()}`)
    );
  }

  /**
   * Get product detail by SupersedeNo and UOM
   */
  static async getProductDetail(
    supersedeNo: string,
    uom: string,
    priceLevel?: string
  ): Promise<ProductDetailResponse> {
    const params = new URLSearchParams();
    if (priceLevel) {
      params.append('priceLevel', priceLevel);
    }

    const response = await api.get(`/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}?${params.toString()}`);
    return response;
  }

  /**
   * List all customer-facing images for a product (ordered by SeqNo).
   */
  static async listProductImages(
    supersedeNo: string,
    uom: string
  ): Promise<Array<{ KeyID: number; SeqNo: number; DocName: string; path: string }>> {
    const response: any = await api.get(
      `/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/images`
    );
    return Array.isArray(response?.data) ? response.data : [];
  }

  /**
   * Search products by term
   */
  static async searchProducts(
    searchTerm: string, 
    priceLevel?: string
  ): Promise<ProductResponse> {
    const params = new URLSearchParams();
    params.append('q', searchTerm);
    if (priceLevel) {
      params.append('priceLevel', priceLevel);
    }

    const cacheKey = `searchProducts:${params.toString()}`;
    return requestThrottler.throttledRequest(cacheKey, () =>
      api.get(`/products/search?${params.toString()}`)
    );
  }

  /**
   * Get products by category
   */
  static async getProductsByCategory(
    category: string, 
    priceLevel?: string
  ): Promise<ProductResponse> {
    const params = new URLSearchParams();
    if (priceLevel) {
      params.append('priceLevel', priceLevel);
    }

    const cacheKey = `getProductsByCategory:${category}:${params.toString()}`;
    return requestThrottler.throttledRequest(cacheKey, () =>
      api.get(`/products/category/${encodeURIComponent(category)}?${params.toString()}`)
    );
  }

  /**
   * Get available categories
   */
  static async getCategories(): Promise<CategoriesResponse> {
    const cacheKey = 'getCategories';
    return requestThrottler.throttledRequest(cacheKey, () =>
      api.get('/products/categories')
    );
  }

  /**
   * Get featured products
   */
  static async getFeaturedProducts(
    priceLevel?: string, 
    limit: number = 6
  ): Promise<ProductResponse> {
    const params = new URLSearchParams();
    if (priceLevel) {
      params.append('priceLevel', priceLevel);
    }
    params.append('limit', limit.toString());

    const response = await api.get(`/products/featured?${params.toString()}`);
    return response;
  }

  /**
   * Validate cart items against current pricing
   */
  static async validateCartItems(request: CartValidationRequest): Promise<CartValidationResponse> {
    const response = await api.post('/products/validate-cart', request);
    return response;
  }

  /**
   * Get product image URL
   */
  static getProductImageUrl(imagePath: string): string {
    if (!imagePath) return '/images/product-placeholder.png';

    // If it already starts with http/https, return as-is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // Replace # with _ in filenames (files were renamed to avoid URL fragment issues)
    // Database may return paths with # or URL-encoded %23, but files are named with _
    // e.g., "PhotoWeb/063958#PKT#1.jpg" or "PhotoWeb/063958%23PKT%231.jpg" -> "PhotoWeb/063958_PKT_1.jpg"
    const normalizedPath = imagePath.replace(/#/g, '_').replace(/%23/g, '_');

    // Use relative URL - nginx serves static files at /api/v1/PhotoWeb/
    if (normalizedPath.startsWith('PhotoWeb/')) {
      return `/api/v1/${normalizedPath}`;
    }

    // Otherwise, construct the URL with api/v1 prefix
    return `/api/v1/${normalizedPath}`;
  }

  /**
   * Format price for display
   */
  static formatPrice(price: number, withCurrency: boolean = true): string {
    const formatted = price.toFixed(2);
    return withCurrency ? `$${formatted}` : formatted;
  }

  /**
   * Strip the trailing GST label (e.g. "w/o gst" / "w/ gst") from a price
   * description so a single price can be shown, keeping the exact amount.
   * "$ 7.50 w/o gst" -> "$ 7.50"
   */
  static priceWithoutGstLabel(priceDesc: string): string {
    return (priceDesc || '').replace(/w\/o\s*gst|w\/\s*gst|w\/gst/gi, '').trim();
  }

  /**
   * Get price level for current user
   */
  static getCurrentPriceLevel(): string {
    // This will be integrated with auth store later
    return 'GN'; // Default to General pricing
  }
}